'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { ArrowPathIcon, ArrowRightIcon, BuildingOffice2Icon } from '@heroicons/react/24/outline';
import { Briefcase, ExternalLink, Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import SafeImage from '@/components/utils/SafeImage';
import { Skeleton } from '@/components/ui/feedback/skeleton';
import { SectionPanel, SectionLoadingSkeleton } from '@/components/ui/design-system';
import {
  ScrollableTabStrip,
  type ScrollableTabStripTab,
} from '@/components/ui/navigation';
import {
  CompanyEntity,
  CompanyEntitySkeleton,
  toCompanyEntityData,
} from '@/components/ui/design-system/entities/company';
import type { CompanyEntityData } from '@/components/ui/design-system/entities/company';
import { ContactEntity, toContactEntityData } from '@/components/ui/design-system/entities/contact';
import { JobEntity, toJobEntityData } from '@/components/ui/design-system/entities/job';
import { ContactGroupBlocks } from '@/app/dashboard/components/contact/ContactGroupBlocks';
import { ConnectedOutreachDialog } from '@/app/dashboard/components/outreach/ConnectedOutreachDialog';
import { ContactQuickViewDialog } from '@/app/dashboard/components/contact/ContactQuickViewDialog';
import { CompanyQuickViewDialog, type CompanyQuickViewData } from '@/app/dashboard/components/company/CompanyQuickViewDialog';
import { InstantContactsDialog } from '@/components/features/reports/InstantContactsDialog';
import { Button } from '@/components/ui/forms/button';
import { groupContactsByCategory } from '@/lib/contacts/contact-groups';
import { profileToContact } from '@/lib/contacts/profile-to-contact';
import type { Contact } from '@/types/report-types';
import type { Job } from '@/types/jobs-types';
import type { UserProfile, QueryUsersParams } from '@/actions/ContactsLookup';
import type { CompaniesListResponse } from '@/types/companies-types';

interface BranchOutCompaniesTabsProps {
  companyNames: string[];
  isLoadingCompanies?: boolean;
  hasPaidAccess?: boolean;
  excludeCompanyName?: string;
  featuredCompanyName?: string;
  onRefresh?: () => void;
  isRefreshingRadar?: boolean;
  /** When true, renders content directly without a SectionPanel wrapper (for nesting inside another panel). */
  embedded?: boolean;
  /** Override the section title (ignored when embedded). */
  title?: string;
  /** Override the section description (ignored when embedded). */
  description?: string;
  /** When true, defers heavy contact lookups until set to false. Company metadata still loads immediately. */
  deferContactLoading?: boolean;
}

type BucketStatus = 'loading' | 'loaded' | 'empty' | 'error';

interface CompanyBucket {
  company: string;
  status: BucketStatus;
  contacts: Contact[];
}

const LOOKUP_SOURCE = 'mixrank_person2_api';
const LOOKUP_DB_TIMEOUT_MS = 60_000;
const CONTACTS_PER_COMPANY = 6;
const MAX_VISIBLE_COMPANIES = 8;
const CACHE_KEY_PREFIX = 'co.brief.company_contacts.v1:';
const CACHE_TTL_MS = 60 * 60 * 1000;

interface CacheEntry {
  cachedAt: number;
  contacts: Contact[];
}

function cacheKey(company: string) {
  return CACHE_KEY_PREFIX + company.trim().toLowerCase();
}

function readCache(company: string): Contact[] | null {
  try {
    const raw = window.localStorage.getItem(cacheKey(company));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CacheEntry;
    if (!parsed?.cachedAt || Date.now() - parsed.cachedAt > CACHE_TTL_MS) return null;
    return Array.isArray(parsed.contacts) ? parsed.contacts : null;
  } catch {
    return null;
  }
}

function writeCache(company: string, contacts: Contact[]) {
  try {
    const entry: CacheEntry = { cachedAt: Date.now(), contacts };
    window.localStorage.setItem(cacheKey(company), JSON.stringify(entry));
  } catch {
    // quota exceeded or storage disabled — not fatal
  }
}

async function lookupDecisionMakers(company: string, signal?: AbortSignal): Promise<Contact[]> {
  const params: QueryUsersParams = {
    source: LOOKUP_SOURCE,
    customFilters: {
      company,
      isCurrentCompany: true,
      useDecisionMakerTitles: true,
    },
    limit: CONTACTS_PER_COMPANY,
    sortBy: 'updated_at',
    sortOrder: 'desc',
    dbQueryTimeout: LOOKUP_DB_TIMEOUT_MS,
    attachmentConfig: {
      fetchEmails: true,
      fetchPhones: false,
      fetchExperience: true,
      fetchEducation: false,
    },
  };

  const response = await fetch('/api/dashboard/contacts/lookup', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mode: 'single', params }),
    signal,
  });

  if (!response.ok) return [];
  const result = await response.json();
  const profiles: UserProfile[] = result?.data?.data ?? [];
  return profiles.map(profileToContact);
}

/**
 * Outcome of a company-metadata lookup. We deliberately distinguish a genuine
 * "no matching company record" (`notfound`) from a transient fetch/DB failure
 * (`error`): a notfound is permanent for this name, so the tab can be skipped,
 * whereas an error may recover and must NOT permanently hide a company. This
 * distinction is what prevents a momentary LinkedIn-DB connection blip from
 * wiping otherwise-valid companies off the radar.
 */
type CompanyMetadataResult =
  | { status: 'found'; data: CompanyEntityData }
  | { status: 'notfound' }
  | { status: 'error' };

async function fetchCompanyMetadata(name: string, signal?: AbortSignal): Promise<CompanyMetadataResult> {
  try {
    const response = await fetch('/api/companies/search', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ company: name, limit: 1, page: 1 }),
      signal,
    });
    if (!response.ok) return { status: 'error' };
    const payload = (await response.json()) as CompaniesListResponse;
    const company = payload?.data?.[0];
    // HTTP 200 with no company === the lookup ran fine and matched nothing
    // (e.g. "Ply Gem" has no row in the LinkedIn company table). Permanent.
    if (!company) return { status: 'notfound' };
    const data = { ...toCompanyEntityData(company), isExactMatch: false };
    if (!data.logoUrl && data.domain) {
      data.logoUrl = `https://logo.clearbit.com/${data.domain}`;
    }
    return { status: 'found', data };
  } catch {
    return { status: 'error' };
  }
}

const JOBS_PER_PAGE = 5;

async function fetchCompanyJobs(company: string, page: number, signal?: AbortSignal): Promise<{ jobs: Job[]; total: number }> {
  try {
    const response = await fetch('/api/dashboard/jobs/search', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ company, limit: JOBS_PER_PAGE, page }),
      signal,
    });
    if (!response.ok) return { jobs: [], total: 0 };
    const result = await response.json();
    return { jobs: result?.data ?? result?.jobs ?? [], total: result?.total ?? 0 };
  } catch {
    return { jobs: [], total: 0 };
  }
}

type BucketOverride = Pick<CompanyBucket, 'status' | 'contacts'>;

const TAB_AVATAR_COLORS = [
  '#7C3AED', '#0EA5E9', '#10B981', '#F59E0B',
  '#EF4444', '#EC4899', '#6366F1', '#14B8A6',
];

function nameToColor(name: string) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return TAB_AVATAR_COLORS[Math.abs(hash) % TAB_AVATAR_COLORS.length];
}

interface TabLogoProps {
  name: string;
  logoUrl?: string;
  size?: number;
}

function TabLogo({ name, logoUrl, size = 18 }: TabLogoProps) {
  const [failed, setFailed] = useState(false);
  const hasLogo = Boolean(logoUrl) && !failed;

  if (hasLogo) {
    return (
      <SafeImage
        src={logoUrl as string}
        alt={name}
        width={size}
        height={size}
        className="rounded-sm border border-border object-cover"
        onError={() => setFailed(true)}
      />
    );
  }

  const letter = name.trim()[0]?.toUpperCase() ?? '?';
  return (
    <span
      aria-hidden="true"
      className="inline-flex items-center justify-center rounded-sm font-bold text-white"
      style={{
        width: size,
        height: size,
        backgroundColor: nameToColor(name),
        fontSize: Math.round(size * 0.6),
        lineHeight: 1,
      }}
    >
      {letter}
    </span>
  );
}

export default function BranchOutCompaniesTabs({
  companyNames,
  isLoadingCompanies = false,
  hasPaidAccess = false,
  excludeCompanyName,
  featuredCompanyName,
  onRefresh,
  isRefreshingRadar = false,
  embedded = false,
  title: titleOverride,
  description: descriptionOverride,
  deferContactLoading = false,
}: BranchOutCompaniesTabsProps) {
  const router = useRouter();

  const normalizedCompanies = useMemo(() => {
    const excluded = excludeCompanyName?.trim().toLowerCase();
    const seen = new Set<string>();
    return companyNames
      .map((name) => name.trim())
      .filter((name) => {
        if (!name) return false;
        const key = name.toLowerCase();
        if (excluded && key === excluded) return false;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .slice(0, MAX_VISIBLE_COMPANIES);
  }, [companyNames, excludeCompanyName]);

  const baseBuckets = useMemo<CompanyBucket[]>(() => {
    return normalizedCompanies.map((company) => {
      const cached = readCache(company);
      if (cached) {
        return { company, status: cached.length > 0 ? 'loaded' : 'empty', contacts: cached };
      }
      return { company, status: 'loading', contacts: [] };
    });
  }, [normalizedCompanies]);

  const [overrides, setOverrides] = useState<Record<string, BucketOverride>>({});
  const [companyEntityDataMap, setCompanyEntityDataMap] = useState<Record<string, CompanyEntityData>>({});
  // Companies whose metadata lookup returned a genuine "no match" (not a
  // transient error). Their tabs are hidden so the user is auto-advanced to the
  // next company instead of staring at a skeleton that will never resolve.
  const [skippedCompanies, setSkippedCompanies] = useState<Set<string>>(() => new Set());

  const buckets = useMemo<CompanyBucket[]>(
    () => baseBuckets.map((b) => ({ ...b, ...overrides[b.company] })),
    [baseBuckets, overrides],
  );

  const visibleBuckets = useMemo(
    () => buckets.filter(
      (b) => (b.status === 'loading' || b.status === 'loaded') && !skippedCompanies.has(b.company),
    ),
    [buckets, skippedCompanies],
  );

  const [activeCompany, setActiveCompany] = useState<string | null>(null);

  useEffect(() => {
    if (visibleBuckets.length === 0) {
      if (activeCompany !== null) setActiveCompany(null);
      return;
    }
    const stillPresent = activeCompany && visibleBuckets.some((b) => b.company === activeCompany);
    if (stillPresent) return;

    const featured = featuredCompanyName?.trim();
    const featuredMatch = featured
      ? visibleBuckets.find((b) => b.company.toLowerCase() === featured.toLowerCase())
      : undefined;
    setActiveCompany((featuredMatch ?? visibleBuckets[0]).company);
  }, [visibleBuckets, featuredCompanyName, activeCompany]);

  // Lazy loading: only fetch contacts + company metadata for the active tab
  useEffect(() => {
    if (!activeCompany) return;

    const bucket = baseBuckets.find((b) => b.company === activeCompany);
    const needsContacts = bucket && bucket.status === 'loading' && !overrides[activeCompany];
    const needsMetadata = !companyEntityDataMap[activeCompany];

    if (!needsContacts && !needsMetadata) return;

    const controller = new AbortController();

    if (needsContacts && !deferContactLoading) {
      void (async () => {
        try {
          const contacts = await lookupDecisionMakers(activeCompany, controller.signal);
          if (controller.signal.aborted) return;
          writeCache(activeCompany, contacts);
          setOverrides((prev) => ({
            ...prev,
            [activeCompany]: { status: contacts.length > 0 ? 'loaded' : 'empty', contacts },
          }));
        } catch (error) {
          if (controller.signal.aborted) return;
          if (error instanceof Error && error.name === 'AbortError') return;
          setOverrides((prev) => ({
            ...prev,
            [activeCompany]: { status: 'error', contacts: [] },
          }));
        }
      })();
    }

    if (needsMetadata) {
      void (async () => {
        const result = await fetchCompanyMetadata(activeCompany, controller.signal);
        if (controller.signal.aborted) return;
        switch (result.status) {
          case 'found':
            setCompanyEntityDataMap((prev) => ({ ...prev, [activeCompany]: result.data }));
            break;
          case 'notfound':
            // Permanent no-match: hide this tab. The auto-advance effect above
            // re-points activeCompany at the next visible company.
            setSkippedCompanies((prev) => {
              if (prev.has(activeCompany)) return prev;
              const next = new Set(prev);
              next.add(activeCompany);
              return next;
            });
            break;
          case 'error':
            // Transient failure (e.g. LinkedIn DB connection blip). Leave
            // needsMetadata true so the lookup retries when this tab is
            // re-opened, rather than permanently hiding a real company.
            break;
        }
      })();
    }

    return () => { controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCompany, deferContactLoading]);

  const navigateToContacts = useCallback(() => {
    router.push('/d/contacts');
  }, [router]);

  const [outreachContact, setOutreachContact] = useState<Contact | null>(null);
  const [quickViewContact, setQuickViewContact] = useState<Contact | null>(null);
  const [companyQuickView, setCompanyQuickView] = useState<CompanyQuickViewData | null>(null);
  const [instantContactsCompany, setInstantContactsCompany] = useState<string | null>(null);

  type SubTab = 'contacts' | 'jobs';
  const [subTabMap, setSubTabMap] = useState<Record<string, SubTab>>({});
  const [jobsMap, setJobsMap] = useState<Record<string, { jobs: Job[]; total: number; loading: boolean }>>({});

  const activeSubTab = (activeCompany && subTabMap[activeCompany]) || 'contacts';

  const loadJobs = useCallback(async (company: string, page: number) => {
    setJobsMap((prev) => ({
      ...prev,
      [company]: { jobs: prev[company]?.jobs ?? [], total: prev[company]?.total ?? 0, loading: true },
    }));
    const result = await fetchCompanyJobs(company, page);
    setJobsMap((prev) => ({
      ...prev,
      [company]: {
        jobs: page === 1 ? result.jobs : [...(prev[company]?.jobs ?? []), ...result.jobs],
        total: result.total,
        loading: false,
      },
    }));
  }, []);

  const handleSubTabChange = useCallback((company: string, tab: SubTab) => {
    setSubTabMap((prev) => ({ ...prev, [company]: tab }));
    if (tab === 'jobs' && !jobsMap[company]) {
      void loadJobs(company, 1);
    }
  }, [jobsMap, loadJobs]);

  const sectionLabel = titleOverride
    ?? (featuredCompanyName
      ? `Branch out from ${featuredCompanyName}`
      : 'Companies on your radar');
  const sectionHint = descriptionOverride
    ?? (featuredCompanyName
      ? 'Same problems → same people doing the hiring.'
      : 'Companies worth a conversation. Pick a target. Pick a person.');

  const refreshButton = onRefresh ? (
    <button
      type="button"
      onClick={onRefresh}
      disabled={isRefreshingRadar}
      title="Refresh companies"
      className="text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40"
    >
      <ArrowPathIcon className={cn('h-3.5 w-3.5', isRefreshingRadar && 'animate-spin')} />
    </button>
  ) : undefined;

  if (normalizedCompanies.length === 0) {
    if (isLoadingCompanies) {
      if (embedded) {
        return (
          <div className="flex items-center gap-3 px-3 py-3 sm:px-4" style={{ minHeight: 360 }}>
            <ArrowPathIcon className="h-3.5 w-3.5 flex-shrink-0 animate-spin text-muted-foreground" />
            <p className="text-xs text-muted-foreground">Scouting companies that match your profile…</p>
          </div>
        );
      }
      return (
        <SectionLoadingSkeleton
          title={sectionLabel}
          description="Scouting companies that match your profile…"
        />
      );
    }

    if (embedded) return null;
    return (
      <SectionPanel
        title={sectionLabel}
        description="No target companies yet. Pick a few — we'll surface decision-makers automatically."
      >
        <Link
          href="/d/research/companies/search"
          className="flex items-center justify-between gap-3 bg-card px-3 py-2.5 transition-colors hover:bg-background/40 sm:px-4"
        >
          <div className="flex min-w-0 items-center gap-2">
            <BuildingOffice2Icon className="h-4 w-4 text-[var(--burgundy-core)]" />
            <span className="text-xs font-bold uppercase tracking-[0.08em] text-foreground">
              Find companies in your market
            </span>
          </div>
          <ArrowRightIcon className="h-4 w-4 shrink-0 text-[var(--burgundy-core)]" />
        </Link>
      </SectionPanel>
    );
  }

  if (
    visibleBuckets.length === 0
    && buckets.every((b) => b.status === 'empty' || b.status === 'error' || skippedCompanies.has(b.company))
  ) {
    if (embedded) return null;
    return (
      <SectionPanel
        title={sectionLabel}
        description="No decision-makers surfaced for these companies yet. Research more in the contacts hub."
      >
        <Link
          href="/d/research/companies/search"
          className="flex items-center justify-between gap-3 bg-card px-3 py-2.5 transition-colors hover:bg-background/40 sm:px-4"
        >
          <div className="flex min-w-0 items-center gap-2">
            <BuildingOffice2Icon className="h-4 w-4 text-[var(--burgundy-core)]" />
            <span className="text-xs font-bold uppercase tracking-[0.08em] text-foreground">
              Find more companies
            </span>
          </div>
          <ArrowRightIcon className="h-4 w-4 shrink-0 text-[var(--burgundy-core)]" />
        </Link>
      </SectionPanel>
    );
  }

  const paywallMode = !hasPaidAccess;

  const renderBucketContent = (bucket: CompanyBucket) => {
    const entityData = companyEntityDataMap[bucket.company];
    if (!entityData) {
      return <CompanyEntitySkeleton layout="card" />;
    }

    const currentSubTab = subTabMap[bucket.company] || 'contacts';
    const companyJobs = jobsMap[bucket.company];

    const contactsContent = bucket.status === 'loading' ? (
      <div className="space-y-1.5" style={{ minHeight: 200 }}>
        <Skeleton className="h-4 w-28" />
        <Skeleton className="h-[72px] w-full" />
        <Skeleton className="h-[72px] w-full" />
      </div>
    ) : bucket.status === 'loaded' && bucket.contacts.length > 0 ? (
      <>
        <ContactGroupBlocks
          groups={groupContactsByCategory(bucket.contacts, (contact) => ({
            contactGroupKey: contact.contactGroupKey,
            contactGroupLabel: contact.contactGroupLabel,
            title: contact.title,
            tags: contact.tags,
            seniority: contact.seniority,
          }))}
          renderContact={(contact) => (
            <ContactEntity
              contact={toContactEntityData(contact)}
              layout="row"
              fields={{ email: true, linkedIn: true, location: true, tags: true, relevance: false, seniority: false, status: true }}
              actions={{ statusPicker: !paywallMode, viewProfile: true, reachOut: true }}
              paywallMode={paywallMode}
              onStatusChange={paywallMode ? undefined : () => navigateToContacts()}
              onViewProfile={paywallMode ? undefined : () => setQuickViewContact(contact)}
              onReachOut={paywallMode ? undefined : () => setOutreachContact(contact)}
            />
          )}
          progressiveDisclosure
          initialVisibleCount={2}
          visibleStep={2}
        />
        <div className="flex justify-center pt-2">
          <Button
            variant="outline"
            size="sm"
            className="text-xs"
            onClick={() => setInstantContactsCompany(bucket.company)}
          >
            Show more contacts
          </Button>
        </div>
      </>
    ) : (
      <p className="py-4 text-center text-xs text-muted-foreground">No contacts found</p>
    );

    const jobsContent = companyJobs?.loading && companyJobs.jobs.length === 0 ? (
      <div className="flex flex-col items-center justify-center gap-2 py-8" style={{ minHeight: 120 }}>
        <ArrowPathIcon className="h-5 w-5 animate-spin text-primary/60" />
        <p className="text-xs text-muted-foreground">Searching for open positions…</p>
      </div>
    ) : companyJobs && companyJobs.jobs.length > 0 ? (
      <div className="space-y-0 divide-y divide-border/40">
        {companyJobs.jobs.map((job) => (
          <JobEntity
            key={job.id ?? job.job_id}
            job={toJobEntityData(job)}
            layout="row"
            density="compact"
            actions={{ apply: true }}
          />
        ))}
        <div className="flex items-center justify-center gap-3 pt-3 pb-1">
          {companyJobs.jobs.length < companyJobs.total && (
            <Button
              variant="outline"
              size="sm"
              className="text-xs"
              disabled={companyJobs.loading}
              onClick={() => {
                const nextPage = Math.floor(companyJobs.jobs.length / JOBS_PER_PAGE) + 1;
                void loadJobs(bucket.company, nextPage);
              }}
            >
              {companyJobs.loading ? 'Loading…' : 'Show more jobs'}
            </Button>
          )}
          <Link
            href={`/d/search?q=${encodeURIComponent(bucket.company + ' jobs')}&filter=jobs`}
            target="_blank"
            className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            Show all jobs
            <ExternalLink className="h-3 w-3" />
          </Link>
        </div>
      </div>
    ) : companyJobs && !companyJobs.loading ? (
      <div className="flex flex-col items-center gap-3 px-6 py-8 text-center">
        <div className="rounded-full bg-muted/60 p-3">
          <Briefcase className="h-6 w-6 text-muted-foreground/60" />
        </div>
        <div className="max-w-xs space-y-1">
          <p className="text-xs font-medium text-foreground/80">No listed positions right now</p>
          <p className="text-[11px] leading-relaxed text-muted-foreground">
            Don&apos;t worry — reaching out directly often uncovers hidden roles that never make it to job boards. It&apos;s still worth making contact.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="mt-1 text-xs"
          onClick={() => handleSubTabChange(bucket.company, 'contacts')}
        >
          <Users className="mr-1.5 h-3 w-3" />
          View contacts instead
        </Button>
      </div>
    ) : null;

    const subTabFooter = (
      <div>
        <div className="flex border-b border-border/40">
          <button
            type="button"
            onClick={() => handleSubTabChange(bucket.company, 'contacts')}
            className={cn(
              'flex items-center gap-1.5 border-b-2 px-4 py-2 text-xs font-medium transition-colors',
              currentSubTab === 'contacts'
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            <Users className="h-3 w-3" />
            Contacts
          </button>
          <button
            type="button"
            onClick={() => handleSubTabChange(bucket.company, 'jobs')}
            className={cn(
              'flex items-center gap-1.5 border-b-2 px-4 py-2 text-xs font-medium transition-colors',
              currentSubTab === 'jobs'
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            <Briefcase className="h-3 w-3" />
            Jobs
          </button>
        </div>
        <div className="pt-2">
          {currentSubTab === 'contacts' ? contactsContent : jobsContent}
        </div>
      </div>
    );

    return (
      <CompanyEntity
        company={entityData}
        layout="card"
        density="compact"
        actions={{ research: false }}
        onQuickView={(company) => setCompanyQuickView({
          id: company.id,
          company_name: company.name,
          website: company.website ?? null,
          industry: company.industry ?? null,
          founded: null,
          domain: company.domain ?? null,
          description: company.description ?? null,
          locality: company.location ?? null,
          employee_count: company.employeeCount ?? null,
          company_type: null,
          country_name: null,
          square_logo_url: company.logoUrl ?? null,
          company_id: company.id,
          isExactMatch: company.isExactMatch,
        })}
        footer={subTabFooter}
        className="border-0 bg-transparent !shadow-none [&>div]:p-0"
      />
    );
  };

  const tabsContent = activeCompany ? (
    <>
      <ScrollableTabStrip<string>
        value={activeCompany}
        onChange={setActiveCompany}
        ariaLabel="Target companies"
        tabs={visibleBuckets.map<ScrollableTabStripTab<string>>((bucket) => ({
          value: bucket.company,
          label: bucket.company,
          leading: (
            <TabLogo
              name={bucket.company}
              logoUrl={companyEntityDataMap[bucket.company]?.logoUrl}
              size={18}
            />
          ),
          labelMaxWidthClass: 'max-w-[140px]',
        }))}
      />
      <div className="mt-3" style={{ minHeight: 320 }}>
        {(() => {
          const activeBucket = visibleBuckets.find((b) => b.company === activeCompany);
          return activeBucket ? renderBucketContent(activeBucket) : null;
        })()}
      </div>
    </>
  ) : null;

  return (
    <>
      {embedded ? (
        <div className="space-y-3 px-3 py-3 sm:px-4" style={{ minHeight: 360 }}>
          {tabsContent}
        </div>
      ) : (
        <SectionPanel
          title={sectionLabel}
          description={sectionHint}
          contentClassName="p-3 sm:p-4 space-y-3"
          trailing={refreshButton}
          collapsible
        >
          {tabsContent}
        </SectionPanel>
      )}

      <ConnectedOutreachDialog
        open={outreachContact !== null}
        onOpenChange={(open) => { if (!open) setOutreachContact(null); }}
        contact={outreachContact}
      />
      <ContactQuickViewDialog
        open={quickViewContact !== null}
        onOpenChange={(open) => { if (!open) setQuickViewContact(null); }}
        contact={quickViewContact}
        onReachOut={(contact) => {
          setQuickViewContact(null);
          setOutreachContact(contact);
        }}
      />
      <CompanyQuickViewDialog
        open={companyQuickView !== null}
        onOpenChange={(open) => { if (!open) setCompanyQuickView(null); }}
        company={companyQuickView}
      />
      <InstantContactsDialog
        open={instantContactsCompany !== null}
        onOpenChange={(open) => { if (!open) setInstantContactsCompany(null); }}
        payload={instantContactsCompany ? { type: 'company', companyName: instantContactsCompany } : null}
        hasPaidAccess={hasPaidAccess}
      />
    </>
  );
}
